<template>
  <div class="global-assessment">
    <h3 class="section-title">Overall Assessment</h3>
    
    <!-- Global Scoring Rubrics -->
    <div class="global-scores">
      <div class="global-score-item">
        <label class="score-label">Overall Performance</label>
        <div class="score-options">
          <button
            v-for="option in overallOptions"
            :key="option.value"
            @click="setGlobalScore('overall', option.value)"
            class="global-score-btn"
            :class="getGlobalScoreClass('overall', option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
      
      <div class="global-score-item">
        <label class="score-label">Communication Skills</label>
        <div class="score-options">
          <button
            v-for="option in communicationOptions"
            :key="option.value"
            @click="setGlobalScore('communication', option.value)"
            class="global-score-btn"
            :class="getGlobalScoreClass('communication', option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
      
      <div class="global-score-item">
        <label class="score-label">Professionalism</label>
        <div class="score-options">
          <button
            v-for="option in professionalismOptions"
            :key="option.value"
            @click="setGlobalScore('professionalism', option.value)"
            class="global-score-btn"
            :class="getGlobalScoreClass('professionalism', option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Global Comments -->
    <div class="global-comments">
      <div class="comment-section">
        <label class="comment-label">Strengths</label>
        <textarea
          v-model="localComments.strengths"
          @input="updateComments"
          placeholder="Note the candidate's key strengths..."
          rows="3"
          class="comment-textarea"
        />
      </div>
      
      <div class="comment-section">
        <label class="comment-label">Areas for Improvement</label>
        <textarea
          v-model="localComments.areas_for_improvement"
          @input="updateComments"
          placeholder="Suggest areas where the candidate can improve..."
          rows="3"
          class="comment-textarea"
        />
      </div>
      
      <div class="comment-section">
        <label class="comment-label">General Feedback</label>
        <textarea
          v-model="localComments.general_feedback"
          @input="updateComments"
          placeholder="Additional comments or observations..."
          rows="2"
          class="comment-textarea"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    globalScores: Object,
    comments: Object
  },
  
  data() {
    return {
      localComments: {
        strengths: '',
        areas_for_improvement: '',
        general_feedback: ''
      },
      overallOptions: [
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'satisfactory', label: 'Satisfactory' },
        { value: 'needs_improvement', label: 'Needs Improvement' },
        { value: 'unsatisfactory', label: 'Unsatisfactory' }
      ],
      communicationOptions: [
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'satisfactory', label: 'Satisfactory' },
        { value: 'needs_improvement', label: 'Needs Improvement' }
      ],
      professionalismOptions: [
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'satisfactory', label: 'Satisfactory' },
        { value: 'needs_improvement', label: 'Needs Improvement' }
      ]
    };
  },
  
  watch: {
    comments: {
      handler(newComments) {
        this.localComments = { ...this.localComments, ...newComments };
      },
      immediate: true
    }
  },
  
  methods: {
    setGlobalScore(type, value) {
      this.$emit('global-score-change', {
        [type]: value
      });
    },
    
    getGlobalScoreClass(type, value) {
      return this.globalScores[type] === value ? 'active' : '';
    },
    
    updateComments() {
      this.$emit('comments-change', this.localComments);
    }
  }
}
</script>

<style scoped>
.global-assessment {
  background: white;
  margin: 0 20px 20px 20px;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.section-title {
  font-size: 1.2em;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
}

.global-scores {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 25px;
}

.global-score-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.score-label {
  font-weight: 500;
  color: #495057;
  min-width: 150px;
  padding-top: 5px;
}

.score-options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
}

.global-score-btn {
  padding: 8px 16px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  background: white;
  color: #495057;
  cursor: pointer;
  font-size: 0.9em;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 120px;
  max-width: 150px;
}

.global-score-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.global-score-btn.active {
  background: #007bff;
  border-color: #007bff;
  color: white;
}

.global-comments {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.comment-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.comment-label {
  font-weight: 500;
  color: #495057;
  font-size: 0.95em;
}

.comment-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.9em;
  resize: vertical;
  transition: all 0.2s ease;
  font-family: inherit;
}

.comment-textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
}

.comment-textarea::placeholder {
  color: #6c757d;
}
</style>